import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import Skeleton from '@components/common/skeleton';
import InfoBlock from '@components/common/info_block';
import { showToast } from '@components/toast';
import { copyStringToClipboard } from '@utils/index';
import {
    apiWebhookService,
    WebhookEndpointItem,
    WebhookDeliverySummary,
    GetWebhookDeliveryOutput,
    GetWebhookDeliveryStatsOutput,
    WebhookEventType,
    CreateWebhookEndpointRequest,
    CreateWebhookEndpointResponse,
    WebhookEnvironment
} from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import CodeBlock from '../_shared/CodeBlock';
import DevPageHeader from '../_shared/PageHeader';
import styles from './styles.module.scss';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTs(iso: string): string {
    return new Date(iso).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function deliveryStatusVariant(status: string): 'success' | 'warning' | 'error' | 'gray' {
    switch (status) {
        case 'delivered':
            return 'success';
        case 'pending':
        case 'delivering':
            return 'warning';
        case 'dead_letter':
            return 'error';
        default:
            return 'gray';
    }
}

function deliveryStatusLabel(status: string): string {
    switch (status) {
        case 'dead_letter':
            return 'Failed';
        default:
            return status.charAt(0).toUpperCase() + status.slice(1);
    }
}

// ─── Create Endpoint Drawer ───────────────────────────────────────────────────

interface CreateEndpointDrawerProps {
    eventTypes: WebhookEventType[];
    isSubmitting: boolean;
    onClose: () => void;
    onSubmit: (req: CreateWebhookEndpointRequest) => void;
}

const CreateEndpointDrawer: FC<CreateEndpointDrawerProps> = ({
    eventTypes,
    isSubmitting,
    onClose,
    onSubmit
}) => {
    const [url, setUrl] = useState('');
    const [description, setDescription] = useState('');
    const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
    const [urlError, setUrlError] = useState('');
    const [eventsError, setEventsError] = useState('');
    const availableEvents = eventTypes.filter((e) => e.status === 'available');

    const toggleEvent = (name: string) => {
        setSelectedEvents((prev) =>
            prev.includes(name) ? prev.filter((e) => e !== name) : [...prev, name]
        );
        setEventsError('');
    };

    const handleSubmit = () => {
        let valid = true;
        if (!url.trim()) {
            setUrlError('Endpoint URL is required.');
            valid = false;
        } else if (!url.startsWith('https://')) {
            setUrlError('Endpoint URL must begin with https://.');
            valid = false;
        } else {
            setUrlError('');
        }
        if (selectedEvents.length === 0) {
            setEventsError('Select at least one event type.');
            valid = false;
        } else {
            setEventsError('');
        }
        if (!valid) return;
        onSubmit({
            url: url.trim(),
            events: selectedEvents,
            description: description.trim() || undefined
        });
    };

    return (
        <div className={styles.drawer_overlay} onClick={onClose}>
            <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.drawer_header}>
                    <h3>Add webhook endpoint</h3>
                    <button className={styles.modal_close} onClick={onClose} disabled={isSubmitting}>
                        <Icon name="close" />
                    </button>
                </div>
                <div className={styles.drawer_body}>
                    <div className={styles.field}>
                        <label htmlFor="wh-url">Endpoint URL</label>
                        <input
                            id="wh-url"
                            type="url"
                            placeholder="https://example.com/webhooks"
                            value={url}
                            onChange={(e) => {
                                setUrl(e.target.value);
                                setUrlError('');
                            }}
                            className={urlError ? styles.error : undefined}
                        />
                        {urlError ? (
                            <span className={styles.field_error}>{urlError}</span>
                        ) : (
                            <span className={styles.field_hint}>
                                Must be a publicly reachable HTTPS URL.
                            </span>
                        )}
                    </div>
                    <div className={styles.field}>
                        <label>Events to subscribe</label>
                        <div className={styles.event_list}>
                            {availableEvents.length === 0 && (
                                <Typography variant="body4">Loading event types…</Typography>
                            )}
                            {availableEvents.map((et) => (
                                <label key={et.name} className={styles.event_checkbox}>
                                    <input
                                        type="checkbox"
                                        checked={selectedEvents.includes(et.name)}
                                        onChange={() => toggleEvent(et.name)}
                                    />
                                    <span>
                                        {et.name}
                                        <small>{et.description}</small>
                                    </span>
                                </label>
                            ))}
                        </div>
                        {eventsError && (
                            <span className={styles.field_error}>{eventsError}</span>
                        )}
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="wh-desc">Description (optional)</label>
                        <input
                            id="wh-desc"
                            type="text"
                            placeholder="e.g. Production payment handler"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={255}
                        />
                    </div>
                    <InfoBlock variant="gray">
                        After creation the signing secret is shown once. Copy and store it securely
                        — it cannot be retrieved again.
                    </InfoBlock>
                </div>
                <div className={styles.modal_footer}>
                    <Button
                        variant="outlined-primary"
                        size="small"
                        onClick={onClose}
                        disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="gold"
                        size="small"
                        onClick={handleSubmit}
                        disabled={isSubmitting}>
                        {isSubmitting ? 'Creating…' : 'Create endpoint'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─── Signing Secret Drawer ────────────────────────────────────────────────────

interface SigningSecretDrawerProps {
    result: CreateWebhookEndpointResponse;
    title?: string;
    gracePeriodNote?: boolean;
    onClose: () => void;
}

const SigningSecretDrawer: FC<SigningSecretDrawerProps> = ({
    result,
    title,
    gracePeriodNote,
    onClose
}) => {
    return (
        // No onClick on the overlay: the secret must survive until the user
        // explicitly confirms they have copied it. Clicking outside must not
        // accidentally dismiss this one-time reveal.
        <div className={styles.drawer_overlay}>
            <div className={styles.drawer}>
                <div className={styles.drawer_header}>
                    <h3>{title ?? 'Endpoint created — copy your signing secret'}</h3>
                    <button className={styles.modal_close} onClick={onClose}>
                        <Icon name="close" />
                    </button>
                </div>
                <div className={styles.drawer_body}>
                    <InfoBlock variant="error">
                        <strong>This secret is shown only once.</strong> Copy and store it in a
                        secure location — you will not be able to retrieve it again. Use it to
                        verify the{' '}
                        <code>X-XEPPT-Signature</code> header on incoming webhook requests.
                    </InfoBlock>
                    {gracePeriodNote && (
                        <InfoBlock variant="warning">
                            Your previous signing secret remains valid for{' '}
                            <strong>24 hours</strong> — update your webhook handler before it
                            expires.
                        </InfoBlock>
                    )}
                    <div className={styles.field}>
                        <label>Signing secret</label>
                        <div className={styles.secret_box}>
                            <code>{result.signing_secret}</code>
                            <button
                                onClick={() => copyStringToClipboard(result.signing_secret)}
                                title="Copy to clipboard">
                                <Icon name="copy" />
                            </button>
                        </div>
                    </div>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Endpoint URL</span>
                        <span className={styles.detail_value}>{result.url}</span>
                    </div>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Events</span>
                        <div className={styles.events_cell}>
                            {result.events.map((ev) => (
                                <span key={ev} className={styles.event_tag}>
                                    {ev}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
                <div className={styles.modal_footer}>
                    <Button variant="gold" size="small" onClick={onClose}>
                        I have copied the secret
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─── Delete Endpoint Drawer ───────────────────────────────────────────────────

interface DeleteEndpointDrawerProps {
    endpoint: WebhookEndpointItem;
    isDeleting: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const DeleteEndpointDrawer: FC<DeleteEndpointDrawerProps> = ({
    endpoint,
    isDeleting,
    onClose,
    onConfirm
}) => {
    return (
        <div className={styles.drawer_overlay} onClick={onClose}>
            <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.drawer_header}>
                    <h3>Delete webhook endpoint</h3>
                    <button
                        className={styles.modal_close}
                        onClick={onClose}
                        disabled={isDeleting}>
                        <Icon name="close" />
                    </button>
                </div>
                <div className={styles.drawer_body}>
                    <Typography variant="body2">
                        Are you sure you want to delete this endpoint? XEPPT will stop delivering
                        events to it immediately. This action cannot be undone.
                    </Typography>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>URL</span>
                        <span className={styles.detail_value}>{endpoint.url}</span>
                    </div>
                    <InfoBlock variant="error">
                        Pending deliveries for this endpoint will not be retried after deletion.
                    </InfoBlock>
                </div>
                <div className={styles.modal_footer}>
                    <Button
                        variant="outlined-primary"
                        size="small"
                        onClick={onClose}
                        disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="outlined-red"
                        size="small"
                        onClick={onConfirm}
                        disabled={isDeleting}>
                        {isDeleting ? 'Deleting…' : 'Delete endpoint'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─── Endpoint Detail Drawer ───────────────────────────────────────────────────

interface EndpointDetailDrawerProps {
    endpoint: WebhookEndpointItem;
    env: WebhookEnvironment;
    onClose: () => void;
    onDelete: (ep: WebhookEndpointItem) => void;
    onRotateSuccess: (endpointId: string, secret: string) => void;
}

const EndpointDetailDrawer: FC<EndpointDetailDrawerProps> = ({
    endpoint,
    env,
    onClose,
    onDelete,
    onRotateSuccess
}) => {
    const [isSendingTest, setIsSendingTest] = useState(false);
    const [testEventError, setTestEventError] = useState<string | null>(null);
    const [testEventSuccess, setTestEventSuccess] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [rotateError, setRotateError] = useState<string | null>(null);
    const [showRotateConfirm, setShowRotateConfirm] = useState(false);
    const isTest = endpoint.environment === 'test';
    const handleSendTestEvent = async () => {
        setIsSendingTest(true);
        setTestEventError(null);
        setTestEventSuccess(false);
        try {
            await apiWebhookService.sendTestEvent(endpoint.id);
            setTestEventSuccess(true);
        } catch {
            setTestEventError('Failed to send test event.');
        } finally {
            setIsSendingTest(false);
        }
    };
    const handleRotate = async () => {
        setIsRotating(true);
        setRotateError(null);
        try {
            const result = await apiWebhookService.rotateSecret(endpoint.id, env);
            onRotateSuccess(result.id, result.signing_secret);
        } catch {
            setRotateError('Failed to rotate signing secret.');
        } finally {
            setIsRotating(false);
        }
    };
    return (
        <div className={styles.drawer_overlay} onClick={onClose}>
            <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.drawer_header}>
                    <h3>Endpoint details</h3>
                    <button className={styles.modal_close} onClick={onClose}>
                        <Icon name="close" />
                    </button>
                </div>
                <div className={styles.drawer_body}>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>URL</span>
                        <span className={styles.detail_value_mono}>{endpoint.url}</span>
                    </div>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Environment</span>
                        <Status variant={isTest ? 'warning' : 'success'}>
                            {isTest ? 'TEST' : 'LIVE'}
                        </Status>
                    </div>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Status</span>
                        <Status variant={endpoint.is_active ? 'success' : 'gray'}>
                            {endpoint.is_active ? 'Active' : 'Inactive'}
                        </Status>
                    </div>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Subscribed events</span>
                        <div className={styles.events_cell}>
                            {endpoint.events.map((ev) => (
                                <span key={ev} className={styles.event_tag}>
                                    {ev}
                                </span>
                            ))}
                        </div>
                    </div>
                    {endpoint.description && (
                        <div className={styles.detail_row}>
                            <span className={styles.detail_label}>Description</span>
                            <span className={styles.detail_value}>{endpoint.description}</span>
                        </div>
                    )}
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Endpoint ID</span>
                        <span className={styles.detail_value_mono}>{endpoint.id}</span>
                    </div>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Created</span>
                        <span className={styles.detail_value}>{formatTs(endpoint.created_at)}</span>
                    </div>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Last updated</span>
                        <span className={styles.detail_value}>{formatTs(endpoint.updated_at)}</span>
                    </div>
                    <div className={styles.detail_row}>
                        <span className={styles.detail_label}>Signing secret</span>
                        <span className={styles.detail_value}>
                            •••••••••••••••••••••••••••
                            <br />
                            <small style={{ color: 'var(--secondary-50)', fontSize: '12px' }}>
                                The signing secret was shown once at creation and cannot be
                                retrieved. Rotate it if it has been compromised.
                            </small>
                        </span>
                    </div>
                    {showRotateConfirm ? (
                        <div
                            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <InfoBlock variant="warning">
                                <strong>Confirm secret rotation.</strong> A new signing secret
                                will be generated immediately. Your{' '}
                                <strong>previous secret stays valid for 24 hours</strong> — update
                                your webhook handler before it expires.
                            </InfoBlock>
                            {rotateError && (
                                <span style={{ color: 'var(--error-50)', fontSize: '13px' }}>
                                    {rotateError}
                                </span>
                            )}
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <Button
                                    variant="outlined-primary"
                                    size="small"
                                    onClick={() => setShowRotateConfirm(false)}
                                    disabled={isRotating}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="outlined-red"
                                    size="small"
                                    onClick={() => void handleRotate()}
                                    disabled={isRotating}>
                                    {isRotating ? 'Rotating…' : 'Confirm rotation'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <Button
                            variant="outlined-primary"
                            size="small"
                            onClick={() => setShowRotateConfirm(true)}>
                            Rotate signing secret
                        </Button>
                    )}
                </div>
                <div className={styles.modal_footer}>
                    {testEventSuccess && (
                        <span style={{ color: 'var(--dp-gold-deep)', fontSize: '13px', marginRight: 'auto' }}>
                            Test event sent — check the delivery log.
                        </span>
                    )}
                    {testEventError && (
                        <span style={{ color: 'var(--error-50)', fontSize: '13px', marginRight: 'auto' }}>
                            {testEventError}
                        </span>
                    )}
                    <Button
                        variant="outlined-primary"
                        size="small"
                        onClick={() => void handleSendTestEvent()}
                        disabled={isSendingTest}>
                        {isSendingTest ? 'Sending…' : 'Send test event'}
                    </Button>
                    <Button
                        variant="outlined-red"
                        size="small"
                        onClick={() => {
                            onClose();
                            onDelete(endpoint);
                        }}>
                        Delete endpoint
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─── Delivery Detail Drawer ───────────────────────────────────────────────────

interface DeliveryDetailDrawerProps {
    delivery: GetWebhookDeliveryOutput | null;
    isLoading: boolean;
    onClose: () => void;
    onRefreshDeliveries: () => void;
}

const DeliveryDetailDrawer: FC<DeliveryDetailDrawerProps> = ({
    delivery,
    isLoading,
    onClose,
    onRefreshDeliveries
}) => {
    const [isReplaying, setIsReplaying] = useState(false);
    const [replayError, setReplayError] = useState<string | null>(null);
    const [replaySuccess, setReplaySuccess] = useState(false);
    const payloadJson = delivery
        ? (() => {
              try {
                  return JSON.stringify(delivery.payload, null, 2);
              } catch {
                  return String(delivery.payload);
              }
          })()
        : '';
    const handleReplay = async () => {
        if (!delivery) return;
        setIsReplaying(true);
        setReplayError(null);
        setReplaySuccess(false);
        try {
            await apiWebhookService.replayDelivery(delivery.id);
            setReplaySuccess(true);
            onRefreshDeliveries();
        } catch {
            setReplayError('Failed to queue the delivery for resend.');
        } finally {
            setIsReplaying(false);
        }
    };
    return (
        <div className={styles.drawer_overlay} onClick={onClose}>
            <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.drawer_header}>
                    <h3>Delivery detail</h3>
                    <button className={styles.modal_close} onClick={onClose}>
                        <Icon name="close" />
                    </button>
                </div>
                <div className={styles.drawer_body}>
                    {isLoading ? (
                        <div className={styles.skeleton_wrapper}>
                            <Skeleton className={styles.skeleton} />
                        </div>
                    ) : delivery ? (
                        <>
                            <div className={styles.detail_row}>
                                <span className={styles.detail_label}>Delivery ID</span>
                                <span className={styles.detail_value_mono}>{delivery.id}</span>
                            </div>
                            <div className={styles.detail_row}>
                                <span className={styles.detail_label}>Event type</span>
                                <span className={styles.event_tag}>{delivery.event_type}</span>
                            </div>
                            <div className={styles.detail_row}>
                                <span className={styles.detail_label}>Status</span>
                                <Status variant={deliveryStatusVariant(delivery.status)}>
                                    {deliveryStatusLabel(delivery.status)}
                                </Status>
                            </div>
                            <div className={styles.detail_row}>
                                <span className={styles.detail_label}>Attempts</span>
                                <span className={styles.detail_value}>
                                    {delivery.attempts} / {delivery.max_attempts}
                                </span>
                            </div>
                            {delivery.response_status != null && (
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Response status</span>
                                    <span className={styles.detail_value}>
                                        {delivery.response_status}
                                    </span>
                                </div>
                            )}
                            {delivery.last_error && (
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Last error</span>
                                    <span className={styles.detail_value}>
                                        {delivery.last_error}
                                    </span>
                                </div>
                            )}
                            {delivery.delivered_at && (
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Delivered at</span>
                                    <span className={styles.detail_value}>
                                        {formatTs(delivery.delivered_at)}
                                    </span>
                                </div>
                            )}
                            {(delivery.status === 'pending' ||
                                delivery.status === 'delivering') && (
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Next attempt</span>
                                    <span className={styles.detail_value}>
                                        {formatTs(delivery.next_attempt_at)}
                                    </span>
                                </div>
                            )}
                            <div className={styles.detail_row}>
                                <span className={styles.detail_label}>Endpoint ID</span>
                                <span className={styles.detail_value_mono}>
                                    {delivery.endpoint_id}
                                </span>
                            </div>
                            {delivery.order_id && (
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Order ID</span>
                                    <span className={styles.detail_value_mono}>
                                        {delivery.order_id}
                                    </span>
                                </div>
                            )}
                            <div className={styles.detail_row}>
                                <span className={styles.detail_label}>Created</span>
                                <span className={styles.detail_value}>
                                    {formatTs(delivery.created_at)}
                                </span>
                            </div>
                            <div className={styles.detail_row}>
                                <span className={styles.detail_label}>Payload</span>
                                <CodeBlock code={payloadJson} language="JSON" json />
                            </div>
                        </>
                    ) : null}
                </div>
                {!isLoading && delivery && (
                    <div className={styles.modal_footer}>
                        {replaySuccess && (
                            <span
                                style={{
                                    color: 'var(--dp-gold-deep)',
                                    fontSize: '13px',
                                    marginRight: 'auto'
                                }}>
                                Delivery queued for resend.
                            </span>
                        )}
                        {replayError && (
                            <span
                                style={{
                                    color: 'var(--error-50)',
                                    fontSize: '13px',
                                    marginRight: 'auto'
                                }}>
                                {replayError}
                            </span>
                        )}
                        <Button
                            variant="outlined-primary"
                            size="small"
                            onClick={() => void handleReplay()}
                            disabled={isReplaying}>
                            {isReplaying ? 'Queuing…' : 'Resend'}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Endpoints Tab ────────────────────────────────────────────────────────────

interface EndpointsTabProps {
    env: WebhookEnvironment;
    onRefreshNeeded: () => void;
}

const EndpointsTab: FC<EndpointsTabProps> = ({ env, onRefreshNeeded }) => {
    const [endpoints, setEndpoints] = useState<WebhookEndpointItem[]>([]);
    const [eventTypes, setEventTypes] = useState<WebhookEventType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [showCreate, setShowCreate] = useState(false);
    const [createdResult, setCreatedResult] = useState<CreateWebhookEndpointResponse | null>(null);
    const [rotatedResult, setRotatedResult] = useState<CreateWebhookEndpointResponse | null>(null);
    const [selectedEndpoint, setSelectedEndpoint] = useState<WebhookEndpointItem | null>(null);
    const [endpointToDelete, setEndpointToDelete] = useState<WebhookEndpointItem | null>(null);

    const fetchEndpoints = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await apiWebhookService.listEndpoints(env);
            setEndpoints(res.endpoints);
        } catch {
            setError('Failed to load webhook endpoints.');
        } finally {
            setIsLoading(false);
        }
    }, [env]);

    const fetchEventTypes = useCallback(async () => {
        try {
            const res = await apiWebhookService.listEventTypes(env);
            setEventTypes(res.event_types);
        } catch {
            // non-fatal — event types are a convenience for the create form
        }
    }, [env]);

    useEffect(() => {
        void fetchEndpoints();
        void fetchEventTypes();
    }, [fetchEndpoints, fetchEventTypes]);

    const handleCreate = async (req: CreateWebhookEndpointRequest) => {
        setIsCreating(true);
        try {
            const result = await apiWebhookService.createEndpoint(req, env);
            setShowCreate(false);
            setCreatedResult(result);
            await fetchEndpoints();
            // Do NOT call onRefreshNeeded() here: it remounts EndpointsTab (via its `key`),
            // which would wipe createdResult and close the one-time signing-secret modal before
            // the merchant can copy it. fetchEndpoints() above already refreshes the list; a newly
            // created endpoint has no deliveries yet, so the delivery log needs no remount.
        } catch {
            showToast.error("Couldn't create the webhook endpoint. Please try again.");
        } finally {
            setIsCreating(false);
        }
    };

    const handleDelete = async () => {
        if (!endpointToDelete) return;
        setIsDeleting(true);
        try {
            await apiWebhookService.deleteEndpoint(endpointToDelete.id, env);
            setEndpointToDelete(null);
            await fetchEndpoints();
            onRefreshNeeded();
        } catch {
            showToast.error("Couldn't delete the webhook endpoint. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRotateSuccess = (endpointId: string, secret: string) => {
        const ep = endpoints.find((e) => e.id === endpointId);
        if (!ep) return;
        // Close the drawer first, then show the secret modal.
        // Do NOT call onRefreshNeeded() — that would bump the key and remount EndpointsTab,
        // wiping rotatedResult and closing the one-time modal before the user copies the secret.
        setSelectedEndpoint(null);
        setRotatedResult({
            id: ep.id,
            url: ep.url,
            events: ep.events,
            description: ep.description,
            environment: ep.environment,
            signing_secret: secret,
            created_at: ep.created_at
        });
    };

    if (isLoading) {
        return (
            <div className={styles.skeleton_wrapper}>
                <Skeleton className={styles.skeleton} />
            </div>
        );
    }

    return (
        <>
            <div className={styles.section_header}>
                <Typography variant="h5">Endpoints</Typography>
                <Button variant="gold" size="small" onClick={() => setShowCreate(true)}>
                    Add endpoint
                </Button>
            </div>

            <div className={styles.table_wrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>URL</th>
                            <th>Events</th>
                            <th>Environment</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {error && (
                            <tr>
                                <td colSpan={6}>
                                    <div className={styles.empty}>
                                        <Icon name="error_outlined" width={40} height={40} />
                                        <Typography variant="body1" weight="bold">
                                            Failed to load endpoints
                                        </Typography>
                                        <Button
                                            variant="outlined-primary"
                                            size="small"
                                            onClick={() => void fetchEndpoints()}>
                                            Retry
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!error && endpoints.length === 0 && (
                            <tr>
                                <td colSpan={6}>
                                    <div className={styles.empty}>
                                        <Icon name="link" width={40} height={40} />
                                        <Typography variant="body1" weight="bold">
                                            No webhook endpoints yet
                                        </Typography>
                                        <Typography variant="body4">
                                            Add an endpoint to start receiving real-time events.
                                        </Typography>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {endpoints.map((ep) => {
                            const isTest = ep.environment === 'test';
                            return (
                                <tr
                                    key={ep.id}
                                    className={styles.clickable}
                                    onClick={() => setSelectedEndpoint(ep)}>
                                    <td>
                                        <span className={styles.url_cell}>{ep.url}</span>
                                    </td>
                                    <td>
                                        <div className={styles.events_cell}>
                                            {ep.events.slice(0, 2).map((ev) => (
                                                <span key={ev} className={styles.event_tag}>
                                                    {ev}
                                                </span>
                                            ))}
                                            {ep.events.length > 2 && (
                                                <span className={styles.event_tag}>
                                                    +{ep.events.length - 2} more
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <Status variant={isTest ? 'warning' : 'success'}>
                                            {isTest ? 'TEST' : 'LIVE'}
                                        </Status>
                                    </td>
                                    <td>
                                        <Status variant={ep.is_active ? 'success' : 'gray'}>
                                            {ep.is_active ? 'Active' : 'Inactive'}
                                        </Status>
                                    </td>
                                    <td>{formatTs(ep.created_at)}</td>
                                    <td className={styles.action_cell}>
                                        <Icon
                                            name="trash"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                setEndpointToDelete(ep);
                                            }}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreate && (
                <CreateEndpointDrawer
                    eventTypes={eventTypes}
                    isSubmitting={isCreating}
                    onClose={() => setShowCreate(false)}
                    onSubmit={(req) => void handleCreate(req)}
                />
            )}

            {createdResult && (
                <SigningSecretDrawer
                    result={createdResult}
                    onClose={() => setCreatedResult(null)}
                />
            )}

            {rotatedResult && (
                <SigningSecretDrawer
                    result={rotatedResult}
                    title="New signing secret — copy before closing"
                    gracePeriodNote
                    onClose={() => setRotatedResult(null)}
                />
            )}

            {selectedEndpoint && (
                <EndpointDetailDrawer
                    endpoint={selectedEndpoint}
                    env={env}
                    onClose={() => setSelectedEndpoint(null)}
                    onDelete={(ep) => {
                        setSelectedEndpoint(null);
                        setEndpointToDelete(ep);
                    }}
                    onRotateSuccess={handleRotateSuccess}
                />
            )}

            {endpointToDelete && (
                <DeleteEndpointDrawer
                    endpoint={endpointToDelete}
                    isDeleting={isDeleting}
                    onClose={() => setEndpointToDelete(null)}
                    onConfirm={() => void handleDelete()}
                />
            )}
        </>
    );
};

// ─── Delivery stats card (S3 / #205) ──────────────────────────────────────────

function fmtPct(rate: number | null): string {
    if (rate == null) return '—';
    return `${(rate * 100).toFixed(1)}%`;
}

function fmtLatency(ms: number | null): string {
    if (ms == null) return '—';
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${Math.round(ms)}ms`;
}

function successRateVariant(rate: number | null): 'success' | 'warning' | 'error' | 'gray' {
    if (rate == null) return 'gray';
    if (rate >= 0.99) return 'success';
    if (rate >= 0.9) return 'warning';
    return 'error';
}

interface DeliveryStatsCardProps {
    stats: GetWebhookDeliveryStatsOutput | null;
    isLoading: boolean;
}

const DeliveryStatsCard: FC<DeliveryStatsCardProps> = ({ stats, isLoading }) => {
    if (isLoading) {
        return (
            <div className={styles.skeleton_wrapper}>
                <Skeleton className={styles.skeleton} />
            </div>
        );
    }
    // Stats are supplementary: on error we render nothing and the delivery log
    // below still works.
    if (!stats) return null;

    const t = stats.totals;
    const terminal = t.delivered + t.failed;
    return (
        <div className={styles.stats_section}>
            <div className={styles.section_header}>
                <Typography variant="h5">Delivery health</Typography>
                <Typography variant="body4" className={styles.stats_caption}>
                    Last {stats.window_hours}h
                </Typography>
            </div>

            <div className={styles.stats_grid}>
                <div className={styles.stat_tile}>
                    <span className={styles.stat_label}>Success rate</span>
                    <span className={styles.stat_value}>{fmtPct(t.success_rate)}</span>
                    <Status variant={successRateVariant(t.success_rate)}>
                        {t.delivered}/{terminal} delivered
                    </Status>
                </div>
                <div className={styles.stat_tile}>
                    <span className={styles.stat_label}>Delivered</span>
                    <span className={styles.stat_value}>{t.delivered}</span>
                    <span className={styles.stat_sub}>of {t.total} total</span>
                </div>
                <div className={styles.stat_tile}>
                    <span className={styles.stat_label}>Failed</span>
                    <span
                        className={`${styles.stat_value} ${
                            t.failed === 0 ? styles.stat_value_muted : ''
                        }`}>
                        {t.failed}
                    </span>
                    <span className={styles.stat_sub}>dead-lettered</span>
                </div>
                <div className={styles.stat_tile}>
                    <span className={styles.stat_label}>Retried</span>
                    <span className={styles.stat_value}>{t.retried}</span>
                    <span className={styles.stat_sub}>needed &gt;1 attempt</span>
                </div>
                <div className={styles.stat_tile}>
                    <span className={styles.stat_label}>Avg latency</span>
                    <span className={styles.stat_value}>{fmtLatency(t.avg_latency_ms)}</span>
                    <span className={styles.stat_sub}>{t.pending} pending</span>
                </div>
            </div>

            {stats.endpoints.length > 0 && (
                <div className={styles.table_wrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Endpoint</th>
                                <th>Success</th>
                                <th>Delivered</th>
                                <th>Failed</th>
                                <th>Retried</th>
                                <th>Avg latency</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.endpoints.map((e) => (
                                <tr key={e.endpoint_id}>
                                    <td>
                                        <span className={styles.endpoint_mono}>
                                            {e.endpoint_id}
                                        </span>
                                    </td>
                                    <td>
                                        <Status variant={successRateVariant(e.success_rate)}>
                                            {fmtPct(e.success_rate)}
                                        </Status>
                                    </td>
                                    <td>{e.delivered}</td>
                                    <td>{e.failed}</td>
                                    <td>{e.retried}</td>
                                    <td>{fmtLatency(e.avg_latency_ms)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ─── Deliveries Tab ───────────────────────────────────────────────────────────

const DELIVERY_PAGE_SIZE = 50;

interface DeliveriesTabProps {
    env: WebhookEnvironment;
}

const DeliveriesTab: FC<DeliveriesTabProps> = ({ env }) => {
    const [deliveries, setDeliveries] = useState<WebhookDeliverySummary[]>([]);
    const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedDelivery, setSelectedDelivery] = useState<GetWebhookDeliveryOutput | null>(
        null
    );
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    // Prevents a fetch that completes after the user closes the loading drawer
    // from reopening the drawer with stale data.
    const loadDetailCancelledRef = useRef(false);

    // Delivery health stats (#205). Supplementary: a failure here never blocks
    // the delivery log below (stats stays null → the card renders nothing).
    const [stats, setStats] = useState<GetWebhookDeliveryStatsOutput | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);

    const fetchStats = useCallback(async () => {
        setIsLoadingStats(true);
        try {
            setStats(await apiWebhookService.getStats({ environment: env }));
        } catch {
            setStats(null);
        } finally {
            setIsLoadingStats(false);
        }
    }, [env]);

    const fetchDeliveries = useCallback(async (after?: string, append = false) => {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);
        setError(null);
        try {
            const res = await apiWebhookService.listDeliveries({
                environment: env,
                limit: DELIVERY_PAGE_SIZE,
                after
            });
            setDeliveries((prev) => (append ? [...prev, ...res.deliveries] : res.deliveries));
            setHasMore(res.has_more);
            setNextCursor(res.next_cursor);
        } catch {
            setError('Failed to load delivery log.');
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [env]);

    useEffect(() => {
        void fetchDeliveries();
        void fetchStats();
    }, [fetchDeliveries, fetchStats]);

    const handleLoadMore = () => {
        if (nextCursor) void fetchDeliveries(nextCursor, true);
    };

    const handleRefresh = () => {
        void fetchDeliveries();
        void fetchStats();
    };

    const handleOpenDetail = async (delivery: WebhookDeliverySummary) => {
        loadDetailCancelledRef.current = false;
        setSelectedDelivery(null);
        setIsLoadingDetail(true);
        try {
            const detail = await apiWebhookService.getDelivery(delivery.id);
            if (!loadDetailCancelledRef.current) {
                setSelectedDelivery(detail);
            }
        } catch {
            // Drawer closes gracefully: isLoadingDetail goes false in finally
            // and selectedDelivery stays null, so the drawer unmounts silently.
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const handleCloseDeliveryDrawer = () => {
        loadDetailCancelledRef.current = true;
        setIsLoadingDetail(false);
        setSelectedDelivery(null);
    };

    if (isLoading) {
        return (
            <div className={styles.skeleton_wrapper}>
                <Skeleton className={styles.skeleton} />
            </div>
        );
    }

    return (
        <>
            <DeliveryStatsCard stats={stats} isLoading={isLoadingStats} />

            <div className={styles.section_header}>
                <Typography variant="h5">Delivery log</Typography>
                <Button variant="outlined-primary" size="small" onClick={handleRefresh}>
                    Refresh
                </Button>
            </div>

            <div className={styles.table_wrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Event type</th>
                            <th>Status</th>
                            <th>Attempts</th>
                            <th>Response</th>
                            <th>Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {error && (
                            <tr>
                                <td colSpan={5}>
                                    <div className={styles.empty}>
                                        <Icon name="error_outlined" width={40} height={40} />
                                        <Typography variant="body1" weight="bold">
                                            Failed to load delivery log
                                        </Typography>
                                        <Button
                                            variant="outlined-primary"
                                            size="small"
                                            onClick={() => void fetchDeliveries()}>
                                            Retry
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!error && deliveries.length === 0 && (
                            <tr>
                                <td colSpan={5}>
                                    <div className={styles.empty}>
                                        <Icon name="info" width={40} height={40} />
                                        <Typography variant="body1" weight="bold">
                                            No deliveries yet
                                        </Typography>
                                        <Typography variant="body4">
                                            Delivery attempts will appear here once events are sent
                                            to your endpoints.
                                        </Typography>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {deliveries.map((d) => (
                            <tr
                                key={d.id}
                                className={styles.clickable}
                                onClick={() => void handleOpenDetail(d)}>
                                <td>
                                    <span className={styles.event_tag}>{d.event_type}</span>
                                </td>
                                <td>
                                    <Status variant={deliveryStatusVariant(d.status)}>
                                        {deliveryStatusLabel(d.status)}
                                    </Status>
                                </td>
                                <td>
                                    {d.attempts} / {d.max_attempts}
                                </td>
                                <td>
                                    {d.response_status != null ? (
                                        <Status
                                            variant={
                                                d.response_status >= 200 &&
                                                d.response_status < 300
                                                    ? 'success'
                                                    : 'error'
                                            }>
                                            {d.response_status}
                                        </Status>
                                    ) : (
                                        <span style={{ color: 'var(--secondary-50)' }}>—</span>
                                    )}
                                </td>
                                <td>{formatTs(d.created_at)}</td>
                            </tr>
                        ))}
                        {isLoadingMore &&
                            Array.from({ length: 3 }).map((_, i) => (
                                <tr key={`skel-${i}`} className={styles.skeleton_row}>
                                    <td colSpan={5}>
                                        <div className={styles.skeleton_line} />
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
                {hasMore && !isLoadingMore && (
                    <div className={styles.load_more_row}>
                        <Button
                            variant="outlined-primary"
                            size="small"
                            onClick={handleLoadMore}
                            disabled={isLoadingDetail}>
                            Load more
                        </Button>
                    </div>
                )}
            </div>

            {(isLoadingDetail || selectedDelivery) && (
                <DeliveryDetailDrawer
                    delivery={selectedDelivery}
                    isLoading={isLoadingDetail}
                    onClose={handleCloseDeliveryDrawer}
                    onRefreshDeliveries={() => void fetchDeliveries()}
                />
            )}
        </>
    );
};

// ─── Main Layout ──────────────────────────────────────────────────────────────

type Tab = 'endpoints' | 'deliveries';

const DevelopersWebhooksLayout = () => {
    const { user } = useUserContext();
    const { env } = useEnvironmentContext();
    const [activeTab, setActiveTab] = useState<Tab>('endpoints');
    const [refreshKey, setRefreshKey] = useState(0);

    // Suppress unused-variable warning: user is consumed to gate rendering
    // exactly like other developer layouts (api_keys, payments) that show
    // a loading state while the user context resolves.
    if (!user) {
        return (
            <motion.div {...pageAnimation} className={styles.wrapper}>
                <Skeleton className={styles.skeleton} />
            </motion.div>
        );
    }

    return (
        <motion.div {...pageAnimation}>
            <div className={styles.wrapper}>
                <DevPageHeader
                    title="Webhooks"
                    subtitle="Manage webhook endpoints and inspect delivery attempts for real-time event notifications."
                />

                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab}${activeTab === 'endpoints' ? ` ${styles.active}` : ''}`}
                        onClick={() => setActiveTab('endpoints')}>
                        Endpoints
                    </button>
                    <button
                        className={`${styles.tab}${activeTab === 'deliveries' ? ` ${styles.active}` : ''}`}
                        onClick={() => setActiveTab('deliveries')}>
                        Delivery log
                    </button>
                </div>

                {activeTab === 'endpoints' && (
                    <EndpointsTab
                        key={`endpoints-${refreshKey}`}
                        env={env}
                        onRefreshNeeded={() => setRefreshKey((k) => k + 1)}
                    />
                )}
                {activeTab === 'deliveries' && (
                    <DeliveriesTab key={`deliveries-${refreshKey}`} env={env} />
                )}
            </div>
        </motion.div>
    );
};

export default DevelopersWebhooksLayout;
